import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { getAuthConfig, isAuthenticated, getUser, setUser } from '../utils/auth';
import '../App.css';
import './LoginPage.css';

function VerifyEmailPage() {
  const navigate = useNavigate();
  const { service } = useParams();
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [resending, setResending] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

  useEffect(() => {
    // Check if user is authenticated
    if (!isAuthenticated()) {
      navigate(service ? `/${service}` : '/login');
      return;
    }

    // Get user email from stored user data
    const user = getUser();
    if (user && user.email) {
      setUserEmail(user.email);
    }
  }, [navigate]);

  const handleVerifyCode = async (e) => {
    if (e) e.preventDefault();

    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    try {
      setIsVerifying(true);
      setError(null);

      const response = await axios.post(
        `${API_URL}/api/auth/verify-email`,
        { code: verificationCode },
        getAuthConfig()
      );

      if (response.data.success) {
        // Mark email as verified in localStorage
        const user = getUser();
        if (user) setUser({ ...user, emailVerified: true });
        setSuccess(true);
        // Redirect to plan selection after 2 seconds
        setTimeout(() => {
          navigate(service ? `/${service}` : '/apps');
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid or expired verification code');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    try {
      setResending(true);
      setError(null);

      const response = await axios.post(
        `${API_URL}/api/auth/resend-verification`,
        {},
        getAuthConfig()
      );

      if (response.data.success) {
        setError('New verification code sent! Please check your inbox.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resend verification code');
    } finally {
      setResending(false);
    }
  };

  if (success) {
    return (
      <div className="checkout-container-new" style={{minHeight:"100vh",background:"#0f0f0f",display:"flex",flexDirection:"column"}}>
        <header className="login-header" style={{flexShrink:0}}>
        <a href="https://openhosst.com" className="login-logo">
          <img src="/logo-dark.png" alt="OpenHosst" />
        </a>
      </header>

        <div className="email-entry-container">
          <div className="email-entry-card">
            <div style={{textAlign: 'center'}}>
              <i className="fa-solid fa-circle-check" style={{fontSize: '64px', color: '#40E0D0', marginBottom: '24px', filter: 'drop-shadow(0 0 20px rgba(64, 224, 208, 0.4))'}}></i>
              <h1 className="email-entry-title">Email Verified!</h1>
              <p className="email-entry-subtitle" style={{marginBottom: '32px'}}>
                Your email has been successfully verified. You can now access all features.
              </p>
              <div className="loading-container" style={{marginBottom: '16px'}}>
                <div className="loading-spinner"></div>
              </div>
              <p className="loading-text">Redirecting to App Store...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-container-new" style={{minHeight:"100vh",background:"#0f0f0f",display:"flex",flexDirection:"column"}}>
      <header className="login-header" style={{flexShrink:0}}>
        <a href="https://openhosst.com" className="login-logo">
          <img src="/logo-dark.png" alt="OpenHosst" />
        </a>
      </header>

      <div className="email-entry-container">
        <div className="email-entry-card">
          <div className="email-entry-icon">
            <i className="fa-solid fa-envelope-circle-check"></i>
          </div>
          <h1 className="email-entry-title">Verify Your Email</h1>
          <p className="email-entry-subtitle">
            We've sent a 6-digit verification code to
          </p>
          <p style={{color: '#40E0D0', fontWeight: '600', marginBottom: '24px'}}>
            {userEmail}
          </p>

          <form onSubmit={handleVerifyCode} className="email-entry-form">
            {error && (
              <div className={`alert ${error.includes('sent') ? 'alert-success' : 'alert-error'}`}>
                {error}
              </div>
            )}

            <div className="form-group-email">
              <label htmlFor="code" className="form-label-new">
                Verification Code
              </label>
              <input
                type="text"
                id="code"
                className="form-input-new form-input-large"
                value={verificationCode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setVerificationCode(value);
                }}
                placeholder="Enter 6-digit code"
                maxLength="6"
                required
                disabled={isVerifying}
                autoFocus
                style={{
                  textAlign: 'center',
                  letterSpacing: '8px',
                  fontSize: '24px',
                  fontWeight: '600',
                  fontFamily: 'monospace'
                }}
              />
              <p className="helper-text-domain">
                Code expires in 15 minutes
              </p>
            </div>

            <button
              type="submit"
              className="btn-continue"
              disabled={isVerifying || verificationCode.length !== 6}
            >
              {isVerifying ? (
                <>
                  <span className="spinner"></span>
                  Verifying...
                </>
              ) : (
                <>
                  Verify Email
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{flexShrink: 0}}>
                    <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </>
              )}
            </button>

            <div style={{textAlign: 'center', marginTop: '20px'}}>
              <button
                type="button"
                onClick={handleResendCode}
                disabled={resending}
                className="btn-link"
                style={{color: '#40E0D0', fontSize: '14px'}}
              >
                {resending ? (
                  <>
                    <span className="spinner" style={{borderTopColor: '#40E0D0'}}></span>
                    Sending...
                  </>
                ) : (
                  "Didn't receive the code? Resend"
                )}
              </button>
            </div>
          </form>

          {/* Attention-grabbing spam folder notice */}
          <div
            style={{
              marginTop: '24px',
              padding: '16px 20px',
              background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.15) 0%, rgba(245, 158, 11, 0.1) 100%)',
              border: '1px solid rgba(251, 191, 36, 0.4)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '14px',
              animation: 'pulse-border 2s ease-in-out infinite'
            }}
          >
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'rgba(251, 191, 36, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                animation: 'bounce 1s ease-in-out infinite'
              }}
            >
              <i
                className="fa-solid fa-folder-open"
                style={{
                  fontSize: '18px',
                  color: '#fbbf24'
                }}
              ></i>
            </div>
            <div style={{flex: 1}}>
              <p style={{
                margin: '0 0 4px 0',
                color: '#fbbf24',
                fontWeight: '600',
                fontSize: '14px'
              }}>
                Can't find the email?
              </p>
              <p style={{
                margin: 0,
                color: '#d4d4d4',
                fontSize: '13px',
                lineHeight: '1.5'
              }}>
                Check your <strong style={{color: '#fbbf24'}}>Spam</strong> or <strong style={{color: '#fbbf24'}}>Junk</strong> folder.
                Sometimes our emails land there by mistake.
              </p>
            </div>
          </div>

          <style>{`
            @keyframes pulse-border {
              0%, 100% {
                box-shadow: 0 0 0 0 rgba(251, 191, 36, 0.4);
              }
              50% {
                box-shadow: 0 0 0 4px rgba(251, 191, 36, 0.1);
              }
            }
            @keyframes bounce {
              0%, 100% {
                transform: translateY(0);
              }
              50% {
                transform: translateY(-3px);
              }
            }
          `}</style>
        </div>
      </div>
    </div>
  );
}

export default VerifyEmailPage;
